import {_, action, autorun, computed, IDisposer, jsondiff, observable, React} from '@api'
import {MenuDivider} from '@blueprintjs/core'
import {AssortmentCardBucket, AssortmentCardContextMenuItems, AssortmentHeroCardsStore, AssortmentLayoutContextMenuItem, AssortmentRootStore, AssortmentSwatchesStore, AssortmentSwatchStore, BuilderCardStore, DefaultCatalogContextMenuItems, FILTER_UNASSIGNED_ID, IContextMenuItems, IHeroCardsOverrides, VipLocalStore} from '@modules'
import {QUERY_KEY_PDF_ARTIFACTS} from '@modules/pdf/serverRenderedPDF/components'
import {createWhiteboardTab, WhiteboardStore} from '@modules/whiteboard'
import {DragDropEventHandlers, ICatalogFieldConfig, StoresInstance, ui} from '@stores'
import {AssortmentCardSettings, DEFAULT_ASSORTMENT_CARD_SETTINGS} from '@stores/catalog'
import stringify from 'json-stable-stringify'
import {reaction, runInAction, when} from 'mobx'
import {computedFn} from 'mobx-utils'
import * as css from './AssortmentCard.css'

export interface IContextualHoverItemStore {
  hoverId: string

  matchesHoverItem(card: BuilderCardStore): boolean
}

export class AssortmentCardStore extends VipLocalStore implements IContextMenuItems {
  @observable.shallow componentStores: Partial<{
    heroCards?: AssortmentHeroCardsStore
    swatches?: AssortmentSwatchesStore
  }> = {}
  @observable everVisible = true // Avoid blank render for animations

  @computed get itemById() {
    const {componentStores: {heroCards}, catalog} = this
    if (heroCards) {
      const items = _.flatMap(heroCards.cardData.map(v => v.items))
      return _.keyBy(items, v => catalog.idFor(v))
    }

    return {}
  }

  constructor(stores: StoresInstance, public props: { bucket?: AssortmentCardBucket, root: AssortmentRootStore }) {
    super(stores)

    this.componentStores.heroCards = new AssortmentHeroCardsStore(this.stores, this)
    if (!this.assortmentCardSettings) {
      const {id, catalog: {settings: {assortment, assortment: {byCard}}}} = this
      byCard[id] = DEFAULT_ASSORTMENT_CARD_SETTINGS
    }

    if (!this.assortmentCardSettings.heroCards?.overrides) {
      _.assign(this.assortmentCardSettings, {heroCards: {overrides: {orders: {}, combinedShapes: []}}})
    }
  }

  @observable checkForIntersection = false

  registerEffects = () => {
    this.root.cardStoresDict[this.id] = this

    setTimeout(() => { this.checkForIntersection = true}, 250)

    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        if (!this.assortmentCardSettings) {
          const {id, catalog: {settings: {assortment: {byCard}}}} = this
          byCard[id] = DEFAULT_ASSORTMENT_CARD_SETTINGS
        }
      }),
      autorun(() => {
        const {catalog, id} = this
        this.ddHandlers = new DragDropEventHandlers(catalog, {id, dragType: `assortmentCard`, targetExtra: {assortmentCard: this}})

        /*
        id: this.idWithPath,
      dragType: this.props.type,
      target: catalog.currentIndex,
      label: _.get(this.props.data, catalog.dataMap.title) || catalog.catalogItemName,
      filename: _.get(this.props.data, catalog.dataMap.title) || catalog.catalogItemName,
      imageUrl: this.imageUrl?.highDefUrl,
      targetExtra: {store: this, data: this.data},
      ignoreEvents: () => this.isCardFieldDragging
         */
      }),
      reaction(() => this.assortmentSettings.swatches.globalSelected, (selected) => {
        if (!selected) {
          this.selectedSwatchStore = null
        }
      }),
      autorun(() => {
        const {overridesId, everVisible} = this
        if (everVisible && overridesId) {
          const {globalOverrides} = this
          if (globalOverrides) {
            _.merge(this.assortmentCardSettings.heroCards.overrides, globalOverrides)
          }
        }
      }),
      reaction(() => {
        const {assortmentSettings: {layout: {heroesPerAssortment, columns}}} = this
        return ({heroesPerAssortment, columns})
      }, () => {
        const {assortmentCardSettings: {heroCards, heroCards: {showAll}}} = this
        if (showAll) {
          heroCards.showAll = false
        }
      })
    ]

    return () => {
      delete this.root.cardStoresDict[this.id]
      _toDispose.forEach(d => d())
    }
  }

  @computed get globalOverrides(): IHeroCardsOverrides {
    return this.root.serverOverrides[this.overridesId]
  }

  set globalOverrides(v) {
    this.root.serverOverrides[this.overridesId] = v
  }

  @action deleteGlobalOverrides = async () => {
    const {overridesId, es} = this
    this.globalOverrides = null
    _.assign(this.assortmentCardSettings.heroCards.overrides, _.cloneDeep(DEFAULT_ASSORTMENT_CARD_SETTINGS.heroCards.overrides))
    await es.delete(overridesId, es.indexes.assortmentOverrides)
  }

  @computed get isOverrideDifferentThanDefault() {
    const d = jsondiff.diff(this.globalOverrides, this.assortmentCardSettings.heroCards.overrides)

    // console.log(d)
    return !_.isEmpty(d)
  }

  @observable.ref ddHandlers: DragDropEventHandlers

  get root() { return this.props.root}

  get dataMap() { return this.root.dataMap}

  get data() { return this.bucket?.item}

  get headerAttributes() { return this.bucket?.headerAttributes}

  get bucket() { return this.props.bucket}

  get key() { return this.bucket?.key}

  get count() { return this.bucket?.doc_count}

  get assortment() {
    return this.catalog.assortments.selectedAssortment
  }

  get config() { return this.assortment}

  @computed get selectedSwatchStore(): AssortmentSwatchStore {
    const {assortmentSettings: {swatches: {globalSelected}}, componentStores: {swatches}} = this
    if (globalSelected) {
      return swatches?.storesById && _.values(swatches.storesById).find(s => s.title === globalSelected)
    } else {
      const {assortmentCardSettings} = this
      return assortmentCardSettings?.swatches?.selected && swatches?.storesById ? swatches.storesById[assortmentCardSettings.swatches.selected] : null
    }
  }

  set selectedSwatchStore(swatch) {
    _.assign(this.assortmentCardSettings, {swatches: {selected: swatch ? swatch.id : null}})

    const {catalog: {settings: {assortment: {swatches}}}} = this
    if (swatches.globalSelected === null || (swatches.globalSelected && swatches.globalSelected != swatch?.swatch?.item?.name)) {
      swatches.globalSelected = undefined
    }
  }

  @computed get selectedPerspective(): ICatalogFieldConfig {
    const {catalog, assortmentCardSettings} = this
    return assortmentCardSettings?.perspective ? catalog._fieldByName[assortmentCardSettings.perspective] : null
  }

  set selectedPerspective(p) {
    _.assign(this.assortmentCardSettings, {perspective: p ? p.field : null} as AssortmentCardSettings)
  }

  countForField = computedFn((field: ICatalogFieldConfig) => {
    if (!field) return this.count

    return this.bucket[field.field]?.value ?? 0
  })

  @computed get items() {
    const {data} = this

    return data
  }

  @computed get headerImageUrl() {
    const {catalog, headerImageRaw} = this
    return headerImageRaw ? catalog.imageUrlFor(headerImageRaw) : null
  }

  @computed get headerImageForeignKeyValue() {
    const {root, data} = this
    return root.getHeaderImageForeignKeyValue(data)
  }

  @computed get headerImageRaw() {
    const {es, data, assortment} = this
    if (!data || !assortment) return null

    if (!assortment?.dataMap?.images) {
      return _.first(data.images)
    }
    const {dataMap: {images}} = assortment
    if (_.isString(images) || (images?.field && !images.foreignField)) {
      const f = _.isString(images) ? images : images.field
      const v = _.get(data, f)
      return _.isArray(v) ? _.first(v) : v
    } else {
      if (images.esIndex) {
        if (images.foreignField) {
          const {headerImageForeignKeyValue} = this

          if (!headerImageForeignKeyValue) return null

          const {root, root: {remoteLookups}} = this, {foreignField, esIndex} = images
          //console.log({field, foreignField, esIndex, foreignKeyValue})

          if (remoteLookups[esIndex]) {
            const dict = remoteLookups[esIndex]
            if (dict[headerImageForeignKeyValue]) {
              const v = dict[headerImageForeignKeyValue]
              return _.isArray(v) ? _.first(v) : v
            }
          }

          setTimeout(() => root.lookupRemoteObject({esIndex, id: headerImageForeignKeyValue, field: foreignField}), 0)
          return null
        }
      }
    }

    return null
  }

  @computed get visibleCardFields(): Array<ICatalogFieldConfig> {
    const {catalog: {defaultFieldsFor, workspaceForField, cv: {visibleCardFields}}, selectedPerspective: field} = this
    let visibleFields = field ? visibleCardFields.filter(f => f.field != field.field && f.field.startsWith(`${field.field}.`)).map(f => ({...f, field: f.field.substring(field.field.length + 1)})) : []
    if (!visibleFields.length && field) {
      visibleFields = defaultFieldsFor(workspaceForField(field))
    }
    return field ? visibleFields : null
  }

  @action onClickPerspective = (field: ICatalogFieldConfig) => {
    this.selectedPerspective = field
  }

  get perspectives() { return this.root.perspectives}

  @computed get mustTerms() {
    const {key} = this
    const r = _.keys(key).map(k => {
      return key[k] === null ? {bool: {must_not: {exists: {field: k}}}}
                             : {term: {[k]: {value: key[k]}}}
    })

    // console.log(r)

    return r
  }

  @action copyQueryToClipboard = () => {
    const {catalog, key, count} = this
    navigator.clipboard.writeText(`GET ${catalog.index}/_search\n${JSON.stringify({
      size: count,
      query: {
        bool: {
          filter: _.keys(key).map(k => ({term: {[k]: {value: key[k]}}}))
        }
      }
    }, null, 2)}`)
  }

  @computed get titleField(): ICatalogFieldConfig {
    const {catalog, dataMap} = this
    return dataMap?.title?.field ? catalog._fieldByName[dataMap.title.field] : null
  }

  @computed get titleRaw() {
    const {data, dataMap: {title}, strings} = this

    return _.get(data, title?.field ?? 'name')
  }

  @computed get title() {
    const {titleRaw, dataMap: {title}, strings} = this
    return (title?.render ? title.render(titleRaw, this.stores) : titleRaw) ?? strings.emptyLabel
  }

  @computed get titleTooltip() {
    return ''

    const {root: {assortment: {grouping}}, data, catalog: {labelForFieldName_currentCatalog}} = this

    return <div className={css.titleTooltip}>
      {grouping.map((f, i) => <div className={css.tooltipRow} key={i}>
        <label>{labelForFieldName_currentCatalog(f)}:</label>
        <span>{_.get(data, f.replace('.exId', 'name').replace('.id', '.name'))}</span>
      </div>)}
    </div>
  }

  @computed get attributes(): Array<{ field: ICatalogFieldConfig, value: any, label: string, valueAsString?: string }> {
    const {data, headerAttributes, dataMap, es, catalog: {labelForFieldName_currentCatalog, _fieldByName}} = this

    let r = (dataMap.attributes ?? []).filter(f => f?.field).map(f => {
      const field = _fieldByName[f.field],
        label = f.label ?? labelForFieldName_currentCatalog(f.field),
        value = _.uniq(headerAttributes.map(v => es.readField(v, f.field)?.valAsString)).join(', ')

      return {field: field ?? {field: f.field}, label, value}
    })

    return r
  }

  onContextMenu = (e) => {
    this.stores.contextMenu.onComponentContextMenu(e, this)
  }

  get contextMenuItems() {
    return (
      <AssortmentCardStoreContext.Provider value={this}>
        <AssortmentCardContextMenuItems/>
        <MenuDivider/>
        <AssortmentLayoutContextMenuItem/>
        <DefaultCatalogContextMenuItems/>
      </AssortmentCardStoreContext.Provider>
    )
  }

  @computed get id() {
    return stringify(this.props.bucket.key)
  }

  /**
   * Unique key for vip-assortment-overrides index
   */
  @computed get overridesId() {
    return stringify({...this.props.bucket.key, workspace: this.catalog.workspace._id})
  }

  get assortmentCardSettings() {
    return this.assortmentSettings.byCard[this.id]
  }

  @computed get showAllHeroes() {
    return this.assortmentCardSettings?.heroCards?.showAll === true
  }

  set showAllHeroes(v) {
    this.assortmentCardSettings.heroCards.showAll = v
  }

  @action toggleShowAllHeroes = () => {
    this.showAllHeroes = !this.showAllHeroes
    this.scrollIntoView()
  }

  get expanded() {
    return this.assortmentCardSettings?.expanded === true
  }

  set expanded(v) {
    this.assortmentCardSettings.expanded = v
  }

  @action toggleExpanded = () => {
    this.assortmentCardSettings.expanded = !this.expanded
    this.scrollIntoView()
  }

  get showInlineHeroCards() {
    return this.assortmentCardSettings?.heroCards?.show === true
  }

  get assortmentSettings() {
    return this.catalog.settings.assortment
  }

  @action toggleShowInlineHeroCards = () => {
    const {showInlineHeroCards, assortmentSettings: {hero: {show: globalShow}}} = this
    _.merge(this.assortmentCardSettings, {heroCards: {show: showInlineHeroCards ? true : globalShow === true ? undefined : false}})
    this.scrollIntoView()
  }

  /// region Filters

  @computed get filterGroupKey() {
    return this.config.dataMap?.title?.field
  }

  @computed get filterValueRaw() {
    const {data, titleField, catalog: {idFor, esFieldFor}} = this
    if (!titleField) return undefined

    return _.get(data, esFieldFor(titleField))
  }

  @computed get filterValue() {
    const {data, titleField, catalog: {idFor, esFieldFor}, filterValueRaw} = this
    if (!titleField) return undefined

    if (filterValueRaw === null) return FILTER_UNASSIGNED_ID

    return titleField.esIndex ? idFor(filterValueRaw) : filterValueRaw
  }

  @computed get filterGroup() {
    const {filter, filterGroupKey} = this
    const r = filter.groupFor(filterGroupKey)
    return _.isEmpty(r) ? null : r
  }

  @computed get isFilterSelected() {
    if (!this.filterGroup) return false
    const {filterGroup: {selected}, filterValue} = this
    return selected?.has(filterValue)
  }

  @computed get isFilterExcluded() {
    if (!this.filterGroup) return false
    const {filterGroup: {excluded}, filterValue} = this
    return excluded?.has(filterValue)
  }

  @action toggleFilterSelected = () => {
    const {filterGroupKey, filter, filterValue} = this
    filter.toggleFilter({key: filterGroupKey, value: filterValue})
  }

  @action toggleFilterExcluded = () => {
    const {filterGroupKey, filter, filterValue} = this
    filter.toggleFilter({key: filterGroupKey, exclude: true, value: filterValue})
  }

  /// endregion
  @observable.ref rootDiv: HTMLElement
  @action setRef = passedRef => divRef => {
    this.rootDiv = divRef
    passedRef && passedRef(divRef)
  }

  @action scrollIntoView = _.debounce(() => {
    const {rootDiv, assortmentSettings: {columnView}, stores: {flexLayoutCatalogRoot: {scrollPanel}}} = this

    if (scrollPanel) {
      scrollPanel.scrollTo({
        top: columnView ? undefined : rootDiv.offsetTop - 30,
        left: !columnView ? undefined : (rootDiv.parentNode as HTMLDivElement).offsetLeft - 30,
        behavior: 'smooth'
      })
    }
  }, ui.settings.flexLayout.scrollIntoViewDelayAfterNewChildTab)

  @observable.ref hoverItem: IContextualHoverItemStore

  hoverItemEventsFor = (store: IContextualHoverItemStore) => {
    return {
      onMouseEnter: (e: React.MouseEvent) => {
        this.hoverItem = store
      },
      onMouseLeave: (e: React.MouseEvent) => {
        if (this.hoverItem === store) {
          this.hoverItem = null
        }
      }
    }
  }

  @action onCopyStoreToWindow = () => {
    window['assortmentCard'] = this
    console.debug(`Copied store for '${this.title}' into window.assortmentCard and your clipboard`)
    navigator.clipboard.writeText(`window.assortmentCard`)
  }

  @observable isShowingHeaderDropdown = false
  @action onHeaderDropdownOpened = () => {
    this.isShowingHeaderDropdown = true
  }

  @action onHeaderDropdownClosed = () => {
    this.isShowingHeaderDropdown = false
  }

  @action onSendToWhiteboard = () => {
    // Do we have a tab we can reuse?
    const {flexLayout: {tabsByComponent}} = this,
      whiteboards = tabsByComponent['whiteboard'] ?? []

    // Try and use an active whiteboard tab, otherwise find one that's not selected, otherwise create one
    let tab = whiteboards.find(w => w.isSelected) ?? _.first(whiteboards)

    if (!tab) {
      tab = createWhiteboardTab()
      tab.move('right')
    }

    tab.selectInTabSet()

    when(
      () => tab.extraData?.whiteboard != null,
      () => {
        const whiteboard = tab.extraData.whiteboard as WhiteboardStore

        runInAction(() => {
          // Create a page if needed
          const page = whiteboard.page ?? whiteboard.createNewPage()

          whiteboard.selectPage(page)

          // Create objects that don't already exist

          const {catalog} = this

          whiteboard.createUtil.createAssortmentCard(this)
        })
      })
  }

  @action onToggleShowHeader = () => this.showHeader = !this.showHeader

  get showHeader() { return this.assortmentCardSettings?.hideHeader !== true}

  set showHeader(v) {
    this.assortmentCardSettings.hideHeader = v !== true
    this.root.settings.hideAllHeaders = false
  }

  @computed get reallyShowHeader() {
    return this.showHeader && !this.root?.settings?.hideAllHeaders
  }

  @action onToggleFooter = () => this.showFooter = !this.showFooter

  get showFooter() { return this.assortmentCardSettings?.hideFooter !== true}

  set showFooter(v) {
    this.assortmentCardSettings.hideFooter = v !== true
    this.root.settings.hideAllFooters = false
  }

  @computed get reallyShowFooter() {
    return this.showFooter && !this.root?.settings?.hideAllFooters
  }

  @computed get reallyShow2ndLevel() {
    return this.isShowingSecondLevel && !this.root?.settings?.hideAll2ndLevel
  }

  @action onZoom = () => this.isZoomed = this.isZoomed !== true

  get isZoomed() { return this.assortmentCardSettings?.zoomed === true}

  set isZoomed(v) { this.assortmentCardSettings.zoomed = v}

  @action onToggleSecondLevel = () => this.isShowingSecondLevel = this.isShowingSecondLevel !== true

  get isShowingSecondLevel() { return this.assortmentCardSettings?.hideSecondLevel !== true}

  @computed get isReallyShowingSecondLevel() {
    return this.isShowingSecondLevel && !this.root.settings?.hideAll2ndLevel
  }

  set isShowingSecondLevel(v) { this.assortmentCardSettings.hideSecondLevel = !v}

  @computed get newPdfFilename() {
    const {config: {pdf}} = this
    if (pdf?.defaultNameForAssortment) {
      return pdf.defaultNameForAssortment(this)
    }

    const {props: {bucket: {key, item}}, catalog: {_fieldByName}, titleField, title, selectedSwatchStore} = this
    let seenTitleField = false,
      r = _.keys(key).map(k => {
        const lookupRoot = k.substring(0, k.lastIndexOf('.')),
          lookup = `${lookupRoot}.name`

        if (lookup === titleField?.field) {
          seenTitleField = true
          return titleField
        }

        if (_fieldByName[lookupRoot]?.pdf?.hideInDefaultFilename !== true
            && _fieldByName[lookup]?.pdf?.hideInDefaultFilename !== true) {
          return _.get(item, lookup)
        }
      }).filter(_.identity).join('-')

    if (!seenTitleField) {
      r = `${title} - ${r}`
    }

    if (selectedSwatchStore) {
      r += ` - ${selectedSwatchStore.title}`
    }

    return r
  }

  @computed get queryVariablesForPDF() {
    const {
        props: {bucket: {key}},
        catalog: {queryVariablesEs}
      } = this,
      query = _.cloneDeep(queryVariablesEs.query)

    _.keys(key).forEach(k => {
      query.bool.filter.push({term: {[k]: key[k]}})
    })

    return query
  }

  @action createPDF = async () => {
    const {
        stores, stores: {authStore: {user: {id: userId}}, cloudPDF, cloudPDF: {prepareGeneratePDF}}, props: {bucket: {key}},
        catalog: {index, queryVariablesEs}, assortmentSettings,
        newPdfFilename, queryVariablesForPDF: query
      } = this

    const r = await prepareGeneratePDF({
      index,
      template: 'assortment',
      userId,
      body: {query},
      settings: assortmentSettings,
      filename: newPdfFilename
    })
  }

  @action addToPDF = () => {

  }
}

export const AssortmentCardStoreContext = React.createContext<AssortmentCardStore>(null)
export const useAssortmentCardStore = () => React.useContext(AssortmentCardStoreContext)

/// endregion


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentCard/AssortmentCardStore.tsx